import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { DragEndEvent } from '@dnd-kit/core'

import { DragHandle } from '../Components/DragHandle'
import { Row, RowProps } from '../Components/Row'
import { Wrapper } from '../Components/Wrapper'

export type DragEndArgs<T> = { oldIndex: number, newIndex: number, item: T, event: DragEndEvent, revert: () => void }

interface DraggableTableOptions<T> {
  fixedRowCount?: number,
  onDragEnd: (args: DragEndArgs<T>) => void,
  enableOptimisticUpdates?: boolean
}

/**
 * Hook to create a draggable table configuration with support for fixed items
 *
 * @param data - Array of data items with id property
 * @param options - Configuration options for the draggable table
 * @returns Table component props for draggable functionality
 *
 * @example
 * ```tsx
 * const { tableProps, DragHandle } = useDraggableTable(data, {
 *   fixedRowCount: 1,
 *   onDragEnd: handleReorder
 * })
 *
 * function handleReorder({ oldIndex, newIndex, item, event, revert }) {
 *   // Send updated position to backend
 * }
 *
 * <Table
 *   {...tableProps}
 *   columns={[
 *     { key: 'drag', render: () => <DragHandle /> },
 *     // ... other columns
 *   ]}
 * />
 * ```
 */
export const useDraggableTable = <T extends { id: string | number }> (
  data: Array<T>,
  options: DraggableTableOptions<T>
) => {
  const { fixedRowCount = 0, onDragEnd, enableOptimisticUpdates = true } = options

  // Internal state for optimistic updates
  const [optimisticData, setOptimisticData] = useState<Array<T> | null>(null)

  // Use optimistic data if available, otherwise use original data
  const displayData = optimisticData || data
  const ids = useMemo(() => displayData.map((item) => item.id), [displayData])

  // Auto-clear optimistic data when original data changes (e.g., after successful API call)
  useEffect(function clearOptimisticDataOnSourceUpdate() {
    if (optimisticData) {
      setOptimisticData(null)
    }
  }, [data])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = displayData.findIndex((item) => item.id === active.id)
    const newIndex = displayData.findIndex((item) => item.id === over.id)

    // Prevent moving items to fixed positions
    if (oldIndex !== -1 && newIndex !== -1) {
      // Don't allow moving to a fixed position
      if (newIndex < fixedRowCount) {
        return
      }

      // Don't allow moving fixed items
      if (oldIndex < fixedRowCount) {
        return
      }

      // Apply optimistic update immediately if enabled
      if (enableOptimisticUpdates) {
        const reorderedData = arrayMove(displayData, oldIndex, newIndex)
        setOptimisticData(reorderedData)
      }

      // Revert function to undo optimistic update
      const revert = () => {
        setOptimisticData(null)
      }

      // Call the original onDragEnd with revert function
      onDragEnd({
        oldIndex: oldIndex - fixedRowCount,
        newIndex: newIndex - fixedRowCount,
        item: displayData[oldIndex],
        event,
        revert
      })
    }
  }, [displayData, fixedRowCount, enableOptimisticUpdates, onDragEnd])

  // Create a custom Row component that passes the necessary props
  const RowWithProps = useCallback((props: RowProps) => (
    <Row
      {...props}
      data-row-index={displayData.findIndex((item) => item.id === props['data-row-key'])}
      data-fixed-item-count={fixedRowCount}
    />
  ), [displayData, fixedRowCount])

  return {
    tableProps: {
      components: {
        body: {
          wrapper: Wrapper({ onDragEnd: handleDragEnd, ids, fixedRowCount }),
          row: RowWithProps
        }
      },
      // Override dataSource to use optimistic data
      dataSource: displayData
    },
    DragHandle
  }
}

/**
 * Utility function to reorder array items
 */
function arrayMove<T>(array: T[], from: number, to: number): T[] {
  const newArray = array.slice()
  newArray.splice(to < 0 ? newArray.length + to : to, 0, newArray.splice(from, 1)[0])
  return newArray
}
